import { useEffect, useState, useMemo } from "react";
import { subscribeOpenHousesForDeal, deleteOpenHouse, updateOpenHouse } from "../../utils/firebasefunctions";
import { deriveStatus, sortOpenHouses } from "../../utils/openHouseHelpers";
import OpenHousesTable from "../open-houses/OpenHousesTable";
import ScheduleModal from "../open-houses/ScheduleModal";
import CancelModal from "../open-houses/CancelModal";
import FeedbackModal from "../open-houses/FeedbackModal";
import RecapPreviewModal from "../open-houses/RecapPreviewModal";
import PostUpForGrabsModal from "../open-houses/PostUpForGrabsModal";
import { sendOpenHouseEmail } from "../../utils/emailSender";
import { composeInviteResendEmail } from "../../utils/openHouseEmailComposer";
import { sendSchedulingEmails } from "../../utils/openHouseSend";
import { feedbackUrlFor } from "../../utils/openHouseEmails";
import { useStateValue } from "../../context/StateProvider";
import { toast } from "react-toastify";
import { useActionDialog } from "../ActionDialog";

export default function DealOpenHousesTab({ deal }) {
  const [{ user, emailTemplates }] = useStateValue();
  const { confirm, prompt, dialog } = useActionDialog();
  const [openHouses, setOpenHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [feedbackTarget, setFeedbackTarget] = useState(null);
  const [recapTarget, setRecapTarget] = useState(null);
  const [postOpen, setPostOpen] = useState(false);

  useEffect(() => {
    if (!deal?.id) return;
    const unsub = subscribeOpenHousesForDeal(deal.id, (rows) => {
      setOpenHouses(rows);
      setLoading(false);
    });
    return () => unsub && unsub();
  }, [deal?.id]);

  const counts = useMemo(() => {
    const c = { Scheduled: 0, Completed: 0, Cancelled: 0 };
    openHouses.forEach((oh) => {
      c[deriveStatus(oh)] = (c[deriveStatus(oh)] || 0) + 1;
    });
    return c;
  }, [openHouses]);

  const sortedRows = useMemo(() => sortOpenHouses(openHouses), [openHouses]);

  const isListing = deal?.dealtype === "Seller";

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-bold text-slate-900">Open Houses</h2>
          <p className="text-xs text-slate-500">
            {openHouses.length} total · {counts.Scheduled} upcoming
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isListing && (
            <button
              onClick={() => setPostOpen(true)}
              className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold px-3 py-2 rounded-md"
            >
              Post Up For Grabs
            </button>
          )}
          <button
            onClick={() => {
              setEditing(null);
              setScheduleOpen(true);
            }}
            disabled={!isListing}
            title={isListing ? "" : "Open houses are scheduled on listings"}
            className="bg-slate-800 hover:bg-slate-900 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold px-3 py-2 rounded-md"
          >
            + Schedule Open House
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <MiniStat label="Upcoming" value={counts.Scheduled} color="text-blue-700" />
        <MiniStat label="Completed" value={counts.Completed} color="text-emerald-700" />
        <MiniStat label="Cancelled" value={counts.Cancelled} />
      </div>

      {loading ? (
        <div className="text-center py-10 text-slate-400 text-sm">Loading…</div>
      ) : (
        <OpenHousesTable
          rows={sortedRows}
          onAddFeedback={setFeedbackTarget}
          onEditFeedback={setFeedbackTarget}
          onEdit={(oh) => {
            setEditing(oh);
            setScheduleOpen(true);
          }}
          onCancel={setCancelTarget}
          onSendRecap={(oh) => setRecapTarget(oh)}
          onSendInvites={async (oh) => {
            const label = oh.propertyAddress?.split(",")[0] || "this open house";
            const ok = await confirm({
              title: "Send notifications",
              body: `Send the open-house notifications for ${label} to the seller and hosting agent now?`,
              confirmLabel: "Send",
            });
            if (!ok) return;
            try {
              await updateOpenHouse(oh.id, { pendingSellerApproval: false }, user.id);
              await sendSchedulingEmails(oh.id, user, emailTemplates);
              toast.success("Notifications sent.");
            } catch (err) {
              console.error("Send invites failed:", err);
              toast.error("Could not send notifications. Try again.");
            }
          }}
          onResendInvite={async (oh) => {
            if (!oh.hostingAgentEmail) {
              toast.error("No hosting agent email on file.");
              return;
            }
            const { subject, text, html } = composeInviteResendEmail(oh, user, undefined, emailTemplates?.["scheduling-agent"]);
            const r = await sendOpenHouseEmail({
              userId: user.id,
              to: oh.hostingAgentEmail,
              toName: oh.hostingAgentName,
              subject, text, html,
              type: "invite-resend",
              relatedDealId: oh.dealId,
              relatedOpenHouseId: oh.id,
            });
            if (r.ok) toast.success("Invite re-sent.");
            else toast.error(`Re-send failed: ${r.error}`);
          }}
          onCopyLink={async (oh) => {
            const url = feedbackUrlFor(oh);
            try {
              await navigator.clipboard.writeText(url);
              toast.success("Feedback link copied to clipboard.");
            } catch {
              // Clipboard blocked. Show the link in a read-only field so it can
              // still be selected and copied by hand.
              await prompt({
                title: "Copy this feedback link",
                label: "Feedback link",
                defaultValue: url,
                readOnly: true,
                confirmLabel: "Done",
              });
            }
          }}
          onDelete={async (oh) => {
            const label = oh.propertyAddress?.split(",")[0] || "this open house";
            const ok = await confirm({
              title: "Delete open house",
              body: `Delete ${label} permanently?`,
              note: "This cannot be undone.",
              tone: "danger",
              confirmLabel: "Delete",
            });
            if (!ok) return;
            try {
              await deleteOpenHouse(oh.id);
              toast.success("Open house deleted.");
            } catch (err) {
              console.error("Delete failed:", err);
              toast.error("Could not delete. Try again.");
            }
          }}
        />
      )}

      <ScheduleModal
        open={scheduleOpen}
        existing={editing}
        lockListing={editing ? null : deal}
        onClose={() => setScheduleOpen(false)}
      />
      <PostUpForGrabsModal
        open={postOpen}
        lockListing={deal}
        onClose={() => setPostOpen(false)}
      />
      <CancelModal
        open={!!cancelTarget}
        openHouse={cancelTarget}
        onClose={() => setCancelTarget(null)}
      />
      <FeedbackModal
        open={!!feedbackTarget}
        openHouse={feedbackTarget}
        onClose={() => setFeedbackTarget(null)}
        onSaved={(updated) => {
          setFeedbackTarget(null);
          setRecapTarget(updated);
        }}
      />
      <RecapPreviewModal
        open={!!recapTarget}
        openHouse={recapTarget}
        onClose={() => setRecapTarget(null)}
      />
      {dialog}
    </div>
  );
}

function MiniStat({ label, value, color = "text-slate-900" }) {
  return (
    <div className="border border-slate-200 rounded-lg bg-slate-50 p-3 text-center">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-lg font-bold ${color}`}>{value}</div>
    </div>
  );
}
